import { AlertCircle } from 'lucide-react'
import { useEffect } from 'react'
import { isRouteErrorResponse, Link, useRouteError } from 'react-router'

import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { Button } from '@/components/ui/button'
import { GenericNotFoundPage } from '@/components/error-boundary/NotFoundPage'
import { getReloadCount, incrementReloadCount, isModuleLoadError } from '@/utils/errors'

const MAX_MODULE_LOAD_RELOADS = 3

export function RootErrorBoundary() {
  const error = useRouteError()
  const is404 = error && isRouteErrorResponse(error) && error.status === 404
  const isLoadError = isModuleLoadError(error)

  useEffect(() => {
    if (isLoadError && getReloadCount(error) < MAX_MODULE_LOAD_RELOADS) {
      incrementReloadCount(error)
      window.location.reload()
    }
  }, [isLoadError])

  if (isLoadError && getReloadCount(error) < MAX_MODULE_LOAD_RELOADS) {
    return null
  }

  if (is404) {
    return <GenericNotFoundPage />
  }

  // For non-404 errors, show a generic error message
  const errorMessage = isRouteErrorResponse(error)
    ? error.statusText || error.data?.message || 'An error occurred'
    : error instanceof Error
      ? error.message
      : 'An unknown error occurred'

  return (
    <div className='flex min-h-screen flex-col'>
      <title>Something went wrong</title>
      <Header />
      <main id='main-content' tabIndex={-1} className='flex flex-1 items-center justify-center px-6'>
        <div className='mx-auto max-w-md text-center'>
          <div className='mb-6 flex justify-center'>
            <div className='rounded-full bg-red-100 p-6'>
              <AlertCircle className='h-12 w-12 text-red-600' />
            </div>
          </div>

          <h1 className='mb-3 text-2xl font-bold text-gray-900'>Something went wrong</h1>
          <p className='mb-6 text-gray-600'>{errorMessage}</p>

          <Link to='/'>
            <Button variant='default'>Go Home</Button>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  )
}
